'use client';

import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import CustomButton from '@/components/CustomButton';
import Link from 'next/link';
import { getAnalytics, logEvent } from "firebase/analytics";

// These are the indexes of the letters to highlight
const highlightSequence = [
    12, 13, 14, // 4 0 4
    26, 27, 28, 29, // P A G E
    37, 38, 39, // N O T
    49, 50, 51, 52, 53, // F O U N D
];

export default function NotFoundPage() {
    const liRefs = useRef<(HTMLLIElement | null)[]>([]);
    const handleClick = () => {
        const analytics = getAnalytics();
        logEvent(analytics, '404_to_home');
    }
    useEffect(() => {
        // Initially show all letters
        gsap.set(liRefs.current, { opacity: 1, backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255)' });

        // Sequentially highlight letters for 404 PAGE NOT FOUND
        highlightSequence.forEach((idx, i) => {
            gsap.to(liRefs.current[idx], {
                backgroundColor: '#f0c869',
                color: '#fff',
                fontWeight: 500,
                duration: 0.5,
                delay: 1 + i * 0.2,
                ease: 'power2.out',
            });
        });
    }, []);

    const letters = [
        'k', 'v', 'n', 'z', 'i', 'x', 'm', 'e', 't', 'a', 'x', 'l',
        '4', '0', '4', 'y', 'y', 'w', 'v', 'b', 'o', 'q', 'd', 'y', 'p', 'a',
        'p', 'a', 'g', 'e', 'v', 'j', 'a', 'c', 'e', 'w', 's', 'n', 'o', 't',
        'v', 'x', 'e', 'p', 'c', 'f', 'h', 'c', 'x', 'f', 'o', 'u', 'n', 'd',
        's', 'w', 'q', 'v', 'o', 's', 'm', 'v', 'f', 'u', 'x', 'd'
    ];

    return (
        <div className="w-full min-h-[calc(100vh-80px)] flex items-center 
        justify-center p-4 relative overflow-hidden">
            {/* Glow background */}
            <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full 
          bg-blue-600/20 blur-[160px] -z-10 pointer-events-none" />

            <div className="flex flex-col md:flex-row w-full max-w-6xl">
                {/* Wordsearch */}
                <div className="w-full md:w-1/2 grid grid-cols-6 gap-1 md:gap-2">
                    {letters.map((char, idx) => (
                        <li
                            key={idx}
                            ref={(el) => { (liRefs.current[idx] = el) }}
                            className="list-none flex items-center justify-center text-white text-2xl md:text-4xl 
                            font-light uppercase rounded transition-colors duration-500"
                        >
                            {char}
                        </li>
                    ))}
                </div>

                {/* Main content */}
                <div className="w-full md:w-1/2 text-white flex flex-col justify-center md:pl-10 mt-10 md:mt-0">
                    <h1 className="text-4xl md:text-5xl font-semibold mb-6">
                        We couldn&apos;t find what you were looking for.
                    </h1>
                    <p className="text-lg md:text-xl mb-6">
                        The page you are trying to access does not exist. Please check the URL or return to the homepage.
                    </p>
                    <Link
                        href="/"
                        onClick={handleClick}
                    >
                        <CustomButton ariaLabel='Go Home' title='Go Home'>
                            Go Home
                        </CustomButton>
                    </Link>
                </div>
            </div>
        </div>
    );
}
